// React's side of an observable object: a component re-renders only when the
// slice it reads changes. Kept in its own entry so the API's tests and other
// non-React code never load React.
import * as React from 'react';

export interface Selectable<S> {
  state(): S;
  subscribe(listener: (state: S) => void): () => void;
}

/**
 * The slice of an object's state a component reads; `undefined` while the
 * object is not there yet. The slice is worked out once per state, so a
 * selector may build a new object or array without causing extra renders.
 */
export function useSelect<S, T>(object: Selectable<S> | undefined, selector: (state: S) => T): T | undefined {
  const cache = React.useRef<{ state?: S; value?: T }>({});
  const subscribe = React.useCallback((notify: () => void) => (object ? object.subscribe(notify) : () => undefined), [object]);
  const read = (): T | undefined => {
    if (!object) return undefined;
    const state = object.state();
    if (cache.current.state !== state) cache.current = { state, value: selector(state) };
    return cache.current.value;
  };
  return React.useSyncExternalStore(subscribe, read, read);
}
